import { type AlgoliaProxyRecord } from '../../algolia/model/AlgoliaProxyRecord'
import {
  type AttributionAddToCartInput,
  attributionFindLatestMatchingItem,
  attributionGetCappedItems,
} from '../attribution'
import {
  EXPERIMENTS_TRACK_EVENT_SEARCH_PRODUCT_CLICK,
  experimentsTrackEvent,
} from '../eventTracking'

import {
  type SearchAttributionCartItem,
  type SearchAttributionClick,
  searchAttributionRead,
  type SearchAttributionStorage,
  type SearchAttributionSurface,
  searchAttributionWrite,
} from './searchAttributionStorage'

export type SearchAttributionAddToCartInput = AttributionAddToCartInput

const searchAttributionGetProductType = ({
  best_offer,
  marketplace,
  own_brand,
}: AlgoliaProxyRecord): SearchAttributionClick['productType'] => {
  if (best_offer?.type === 'MIRAKL' || (!best_offer && marketplace)) {
    return 'marketplace'
  }

  if (own_brand) {
    return 'own-brand'
  }

  return 'sae'
}

export const searchAttributionRecordProductClick = ({
  position,
  product,
  surface,
}: {
  position: number
  product: AlgoliaProxyRecord
  surface: SearchAttributionSurface
}): void => {
  if (!product.upid) {
    return
  }

  const productType = searchAttributionGetProductType(product)
  const sponsored = Boolean(product.adServerMetadata)
  const storage = searchAttributionRead()
  const offerId = product.offerId ?? product.best_offer?.id

  searchAttributionWrite({
    ...storage,
    clicks: attributionGetCappedItems([
      ...storage.clicks,
      {
        clickId: crypto.randomUUID(),
        createdAt: Date.now(),
        ...(offerId && { offerId }),
        productId: product.upid,
        productType,
        surface,
      },
    ]),
  })

  experimentsTrackEvent(EXPERIMENTS_TRACK_EVENT_SEARCH_PRODUCT_CLICK, {
    $opt_event_properties: {
      position,
      productType,
      sponsored,
      surface,
    },
  })
}

const getAttributedCartItem = (
  {
    offerId,
    productFamilyIds,
    productId,
    quantity,
  }: SearchAttributionAddToCartInput,
  storage: SearchAttributionStorage,
): SearchAttributionCartItem | undefined => {
  const click = attributionFindLatestMatchingItem(storage.clicks, {
    offerId,
    productFamilyIds,
    productId,
  })

  if (!click) {
    return undefined
  }

  const resolvedOfferId = offerId ?? click.offerId

  return {
    clickId: click.clickId,
    createdAt: Date.now(),
    ...(resolvedOfferId && { offerId: resolvedOfferId }),
    productId: productId ?? click.productId,
    productType: click.productType,
    quantity,
    surface: click.surface,
  }
}

export const searchAttributionRecordProductsAddToCart = (
  products: SearchAttributionAddToCartInput[],
): (SearchAttributionCartItem | undefined)[] => {
  const storage = searchAttributionRead()
  const attributedCartItems = products.map((product) =>
    getAttributedCartItem(product, storage),
  )
  const cartItems = attributedCartItems.filter(
    (cartItem): cartItem is SearchAttributionCartItem => Boolean(cartItem),
  )

  if (cartItems.length > 0) {
    searchAttributionWrite({
      ...storage,
      cartItems: attributionGetCappedItems([
        ...storage.cartItems,
        ...cartItems,
      ]),
    })
  }

  return attributedCartItems
}
